import { useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { languages, languageOrder } from '../config/languages';
import { useLanguage } from '../hooks/useLanguage';
import { pathIn, setPreferredLanguage } from '../utils/languagePreference';

export default function LanguageSwitcher() {
  const currentLang = useLanguage();
  const location = useLocation();
  const descId = useId();

  // Each toggle is a link to the same page in the other language, with
  // hreflang (SR-7): it navigates, so screen readers announce it as a link and
  // it can be opened in a new tab. It is labelled in its own language
  // ("Cymraeg", "English"), so it carries lang={code} for correct
  // pronunciation (WCAG 3.1.2). Its name stays the visible label (2.5.3); a
  // description in the page's own language says what it does (2.4.6).
  // Choosing a language is a setting, not a step through the site: it is saved
  // (setPreferredLanguage) and replaces the history entry, so Go back browses to
  // the previous page, in the chosen language, instead of undoing the choice.
  // The entry's router state is kept, with `from` (the list a collection was
  // opened from, used by "Back to …") moved to the new language as well.
  // data-nav-id lets ScrollToTop put focus back on it after Back (2.4.3).
  return (
    <div className="flex gap-2">
      <span id={descId} className="sr-only">{currentLang.ui.changeLanguage}</span>
      {languageOrder
        .filter(code => code !== currentLang.code)
        .map(code => (
          <Link
            key={code}
            to={pathIn(code, location)}
            replace
            state={location.state?.from
              ? { ...location.state, from: pathIn(code, { pathname: location.state.from, search: '' }) }
              : location.state}
            onClick={() => setPreferredLanguage(code)}
            lang={code}
            hrefLang={code}
            aria-describedby={descId}
            data-nav-id={`lang-${code}`}
            className="lang-btn"
          >
            {languages[code].name}
          </Link>
        ))}
    </div>
  );
}
